import type { MiceTraveler } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, IconButton } from '@web/ui/core';
import { eligibleIds, outOfActions, type MiceBulkAction } from '../travelers/eligibility';

const PURCHASE_ACTIONS: readonly MiceBulkAction[] = [
  'recommend',
  'approve',
  'buy',
  'retry',
  'unqueue',
  'manual',
  'profile_sheet',
  'profile_keep',
];

/** As que criam, recomendam ou põem compra na fila: saem com as compras do evento encerradas. */
const BUYING_ACTIONS: ReadonlySet<MiceBulkAction> = new Set(['recommend', 'buy', 'retry']);

interface SelectionBarProps {
  selected: readonly MiceTraveler[];
  canPurchase: boolean;
  /** Compras do evento abertas (o organizador pode encerrá-las pelo chat). */
  purchasesOpen: boolean;
  canManage: boolean;
  /** Premissas conferidas: sem elas, recomendar fica desabilitado com o motivo. */
  premisesReady: boolean;
  busy: boolean;
  onAction: (action: MiceBulkAction, ids: string[]) => void;
  onClear: () => void;
}

/** Ações da seleção: só as que valem para ela, com a contagem, e quantos ficam de fora. */
export function SelectionBar(props: Readonly<SelectionBarProps>) {
  if (props.selected.length === 0) return null;
  const offered = [
    ...(props.canPurchase
      ? PURCHASE_ACTIONS.filter((a) => props.purchasesOpen || !BUYING_ACTIONS.has(a))
      : []),
    ...(props.canManage ? (['remove'] as const) : []),
  ];
  const actions = offered
    .map((action) => ({ action, ids: eligibleIds(action, props.selected) }))
    .filter((a) => a.ids.length > 0);
  const out = outOfActions(props.selected, offered);
  const blockRecommend = !props.premisesReady;
  return (
    <div
      role="region"
      aria-label={t('mice.selection.label')}
      className="sticky bottom-4 z-10 flex flex-wrap items-center gap-2 self-center rounded-lg bg-foreground px-4 py-2.5 text-background shadow-lg"
    >
      <span className="text-meta font-medium">
        {t('mice.selection.count', { count: props.selected.length })}
      </span>
      {actions.map(({ action, ids }) => (
        <Button
          key={action}
          size="sm"
          variant={action === 'approve' || action === 'buy' ? 'primary' : 'outline'}
          disabled={props.busy || (action === 'recommend' && blockRecommend)}
          onClick={() => {
            props.onAction(action, ids);
          }}
        >
          {t(`mice.selection.action.${action}`, { count: ids.length })}
        </Button>
      ))}
      {blockRecommend && actions.some((a) => a.action === 'recommend') ? (
        <span className="text-micro">{t('mice.travelers.premisesFirst')}</span>
      ) : null}
      {out > 0 ? (
        <span className="text-micro opacity-80">{t('mice.selection.out', { count: out })}</span>
      ) : null}
      <IconButton
        icon="close"
        variant="ghost"
        size="sm"
        aria-label={t('mice.selection.clear')}
        title={t('mice.selection.clear')}
        onClick={props.onClear}
      />
    </div>
  );
}
